import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Gift, X } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import confetti from 'canvas-confetti';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const OFFER_EXPIRY = new Date('2026-03-31T23:59:59Z');
const STORAGE_KEY = 'promo_offer_dismissed';
const SESSION_KEY = 'promo_offer_shown_pages';
const DISMISS_DURATION = 2 * 60 * 60 * 1000; // 2 hours in ms
const OFFER_CODE = 'STORILY50';

interface PromoOfferModalProps {
  triggerRef?: React.RefObject<HTMLElement>;
}

const PromoOfferModal: React.FC<PromoOfferModalProps> = ({ triggerRef }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  
  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [copied, setCopied] = useState(false);

  // Track whether the modal has ever actually been opened.
  // (Some dialog implementations can emit an initial onOpenChange(false) on mount;
  // we must not treat that as a user dismissal.)
  const hasEverOpenedRef = useRef(false);

  // Netlify SSG prerender runs in headless Chrome. If we allow the modal to open
  // during prerender, the generated HTML ships with the popup already visible,
  // which then causes hydration/state mismatches and a "stuck" modal on live.
  const isHeadlessPrerender =
    typeof navigator !== 'undefined' && /HeadlessChrome/i.test(navigator.userAgent);

  // Wait for hydration before showing modal
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Reset hasEverOpenedRef when navigating to a new page
  // This allows the modal to potentially show again on new pages
  useEffect(() => {
    hasEverOpenedRef.current = false;
  }, [location.pathname]);

  // Check if user has dismissed and show after a short delay
  useEffect(() => {
    if (!isMounted) return;

    // Never open during headless prerender (Netlify/puppeteer SSG)
    if (isHeadlessPrerender) return;
    
    // Check for permanent dismissal with 2-hour expiry
    const dismissedAt = localStorage.getItem(STORAGE_KEY);
    if (dismissedAt) {
      const dismissTime = parseInt(dismissedAt, 10);
      if (Date.now() - dismissTime < DISMISS_DURATION) {
        return; // Still within 2-hour window
      }
      // 2 hours passed, clear the dismissal
      localStorage.removeItem(STORAGE_KEY);
    }
    
    // Check if already shown on this page this session
    const shownPages = sessionStorage.getItem(SESSION_KEY) || '';
    if (shownPages.includes(location.pathname)) {
      return;
    }
    
    const openModal = () => {
      hasEverOpenedRef.current = true;
      setIsOpen(true);
      const updatedPages = shownPages 
        ? `${shownPages},${location.pathname}` 
        : location.pathname;
      sessionStorage.setItem(SESSION_KEY, updatedPages);
    };

    // If triggerRef provided, use IntersectionObserver; otherwise fall back to 3s timer
    if (triggerRef?.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            openModal();
            observer.disconnect();
          }
        },
        { threshold: 0.3 }
      );
      observer.observe(triggerRef.current);
      return () => observer.disconnect();
    } else {
      const timer = setTimeout(openModal, 3000);
      return () => clearTimeout(timer);
    }
  }, [isMounted, isHeadlessPrerender, location.pathname, triggerRef]);

  // Countdown timer
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = OFFER_EXPIRY.getTime();
      const difference = target - now;

      if (difference > 0) {
        return {
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        };
      }
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleDismiss = useCallback(() => {
    // Just close - no permanent storage (session tracking already handled)
    setIsOpen(false);
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      // Always keep state in sync.
      setIsOpen(open);

      // Only mark as dismissed if the user actually closed an opened modal.
      if (!open && hasEverOpenedRef.current) {
        handleDismiss();
      }
    },
    [handleDismiss]
  );

  const handleGetOffer = () => {
    // Fire confetti
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });

    // Store timestamp for 2-hour expiry
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setIsOpen(false);

    // Navigate to pricing if not already there, then scroll to pricing table
    if (location.pathname !== '/pricing') {
      navigate('/pricing');
      setTimeout(() => {
        const pricingSection = document.getElementById('pricing-tables');
        if (pricingSection) {
          pricingSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500);
    } else {
      const pricingSection = document.getElementById('pricing-tables');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(OFFER_CODE);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Theme colors
  const modalBg = isDark ? '#1A1A1A' : '#FFFFFF';
  const headingColor = isDark ? '#FFFFFF' : '#111111';
  const textColor = isDark ? '#AAAAAA' : '#555555';
  const countdownBg = isDark ? '#222222' : '#F0F0F0';
  const accentGradient = 'linear-gradient(135deg, #0d7680 0%, #14a3b0 100%)';
  const btnBg = isDark ? '#FFFFFF' : '#111111';
  const btnText = isDark ? '#111111' : '#FFFFFF';
  const codeBg = isDark ? '#333333' : '#F8F8F8';
  const codeBorder = isDark ? '#444444' : '#E0E0E0';

  // Don't render until mounted (prevents hydration issues on SSG)
  if (!isMounted) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent 
        className="max-w-[90vw] sm:max-w-md max-h-[85vh] sm:max-h-none overflow-y-auto p-0 rounded-2xl border-0 overflow-hidden [&>button:last-child]:hidden"
        style={{ 
          backgroundColor: modalBg,
          boxShadow: isDark 
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)' 
            : '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}
      >
        {/* Gradient header */}
        <div 
          className="px-4 sm:px-6 py-6 sm:py-8 text-center relative"
          style={{ background: accentGradient }}
        >
          <button
            type="button"
            onClick={handleDismiss}
            className="absolute top-2 right-2 sm:top-3 sm:right-3 p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors z-10"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-white" />
          </button>
          
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-white/20 mb-3 sm:mb-4">
            <Gift className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
          </div>
          
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-1 sm:mb-2">
            Exclusive Offer
          </h2>
          <p className="text-white/80 text-xs sm:text-sm">
            Limited time only - Don't miss out!
          </p>
        </div>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
          {/* Offer details */}
          <div className="text-center space-y-2 sm:space-y-3">
            <div className="flex items-center justify-center gap-1.5 sm:gap-2">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: '#0d7680' }} />
              <span 
                className="text-3xl sm:text-4xl font-black"
                style={{ 
                  background: accentGradient,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                50% OFF
              </span>
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: '#14a3b0' }} />
            </div>
            
            <p className="text-xs sm:text-sm" style={{ color: textColor }}>
              Your first month after free trial<br />
              <span className="font-semibold" style={{ color: headingColor }}>OR your entire first year</span> with annual billing
            </p>
          </div>

          {/* Offer code */}
          <div 
            className="flex items-center justify-between p-2.5 sm:p-3 rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
            style={{ 
              backgroundColor: codeBg,
              border: `1px dashed ${codeBorder}`
            }}
            onClick={handleCopyCode}
          >
            <div>
              <span className="text-[10px] sm:text-xs uppercase tracking-wider" style={{ color: textColor }}>
                Use code
              </span>
              <p className="font-mono font-bold text-base sm:text-lg" style={{ color: headingColor }}>
                {OFFER_CODE}
              </p>
            </div>
            <Badge 
              variant="secondary" 
              className="text-[10px] sm:text-xs"
              style={{ 
                backgroundColor: isDark ? '#444' : '#E0E0E0',
                color: headingColor
              }}
            >
              {copied ? 'Copied!' : 'Click to copy'}
            </Badge>
          </div>

          {/* Countdown timer */}
          <div className="space-y-1.5 sm:space-y-2">
            <p className="text-[10px] sm:text-xs text-center uppercase tracking-wider" style={{ color: textColor }}>
              Offer expires 31st March 2026
            </p>
            <div className="flex justify-center gap-1.5 sm:gap-2">
              {[
                { value: timeLeft.days, label: 'days' },
                { value: timeLeft.hours, label: 'hrs' },
                { value: timeLeft.minutes, label: 'mins' },
                { value: timeLeft.seconds, label: 'secs' }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div 
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center text-base sm:text-lg font-bold overflow-hidden"
                    style={{ backgroundColor: countdownBg, color: headingColor }}
                  >
                    <span 
                      key={item.value} 
                      className="inline-block animate-[countdown-flip_0.3s_ease-out]"
                    >
                      {String(item.value).padStart(2, '0')}
                    </span>
                  </div>
                  <span className="text-[8px] sm:text-[9px] mt-0.5 sm:mt-1 block" style={{ color: textColor }}>
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Button */}
          <button
            type="button"
            onClick={handleGetOffer}
            className="w-full py-2.5 sm:py-3 text-xs sm:text-sm font-semibold rounded-xl transition-all duration-200 hover:scale-[1.02] hover:opacity-90 active:scale-[0.98] flex items-center justify-center gap-2"
            style={{ background: accentGradient, color: '#FFFFFF' }}
          >
            <Gift className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            Get Offer
          </button>

          <p className="text-[9px] sm:text-[10px] text-center" style={{ color: textColor }}>
            *Applies to new subscriptions only. Cannot be combined with other offers.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PromoOfferModal;
